import { CameraGroup, WatchList } from '@/api';
import { dataModule } from '@/store/data';
import { NewItemIdStart } from '@/store/multisidebar';
import { ISelectItem, NInputNumber, NSelect, NSwitch } from '@/uikit';
import NCheckbox from '@/uikit/checkbox/NCheckbox.vue';
import { IFormContext, IFormModel } from '@/uikit/forms/NForm.vue';
import { IFormLayoutBlocks } from '@/uikit/forms/NFormBlocks.vue';
import { getNumberValidator, getRequiredValidator } from '@/uikit/forms/validators';
import NInput from '@/uikit/input/NInput.vue';
import { getItemCommonFields } from '@/components/common/get-item-common-fields';
import ObjectThresholds from '@/components/common/ObjectThresholds.vue';
import { configModule } from '@/store/config';
import NTextDelimiter from '@/uikit/text/NTextDelimiter.vue';
import NConfidence from '@/uikit/confidence/NConfidence.vue';
import { settingsItemModule } from '@/store/data/SettingsItemModule';
import ExtendedEventAnalysisExtraSettings from '@/components/common/ExtendedEventAnalysisExtraSettings.vue';

const cameraGroupInformation: IFormLayoutBlocks = [
  {
    layout: [
      {
        classes: 'label-m n-form-w-6 n-form-pad-10',
        component: NTextDelimiter,
        props: { i18n_label: 'common.general_plural' }
      },
      [
        {
          path: 'name',
          classes: 'n-form-w-3 n-form-pad-10',
          i18n_label: 'common.name',
          component: NInput,
          validators: [getRequiredValidator()]
        },
        {
          classes: 'n-form-w-3 n-form-pad-10',
          i18n_label: 'common.labels',
          component: NSelect,
          props: {
            multiple: true,
            allowCreate: true,
            keyField: '',
            i18n_placeholder: 'common.camera_groups_not_selected',
            loadItems: () => dataModule.cameraGroupsLabels.map((v) => ({ label: v, value: v }))
          },
          encode: function(this: IFormContext, model: IFormModel, value: ISelectItem[]) {
            let result: any = {};
            value.forEach((item) => {
              result[item.label] = 'true';
            });
            this.model.labels = result;
          },
          decode: function(this: IFormContext) {
            let value: ISelectItem[] = [];
            Object.keys(this.model?.labels || []).forEach((label, index) =>
              value.push({
                label: label,
                value: label
              })
            );
            return value;
          }
        }
      ],
      {
        path: 'comment',
        classes: 'label-m n-form-w-6 n-form-pad-10',
        i18n_label: 'common.comment',
        component: NInput
      },
      [
        {
          path: 'deduplicate',
          classes: 'label-m n-form-w-4 n-form-vcenter n-form-pad-10',
          component: NCheckbox,
          props: { i18n_label: 'common.deduplicate_events_with_interval' }
        },
        {
          path: 'deduplicateDelay',
          classes: 'label-m n-form-w-2 n-form-pad-10',
          component: NInputNumber,
          validators: [getRequiredValidator()]
        }
      ],
      {
        classes: 'label-m n-form-w-6 n-form-pad-10',
        component: NTextDelimiter,
        props: { i18n_label: 'common.confidence' }
      },
      {
        component: ObjectThresholds,
        classes: 'n-form-w-6 n-form-pad-20 control-m',
        props: (item: WatchList) => ({ item })
      }
    ]
  }
];

const bottomLayout = [
  {
    layout: getItemCommonFields(),
    display: (model: any) => {
      return model.id > NewItemIdStart;
    }
  }
];

const extendedEventAnalysisActiveLayout = [
  {
    layout: [
      {
        classes: 'label-m n-form-w-6 n-form-pad-10',
        component: NTextDelimiter,
        props: { i18n_label: 'common.extended_event_analysis' }
      },
      {
        classes: 'n-form-w-6 n-form-pad-20 control-m',
        component: NSwitch,
        props: { i18n_label: 'common.enable_extended_event_analysis' },
        decode: function(this: IFormContext) {
          return !!this.model.extended_event_analysis?.active;
        },
        encode: function(this: IFormContext, _model: IFormModel, value: number) {
          this.model.extended_event_analysis.active = value;
        }
      }
    ],
    display: (model: any) => {
      return configModule.features.extended_event_analysis_enabled && dataModule.isCurrentUserAdmin && model.id !== -1;
    }
  }
];

const extendedEventAnalysisFieldsLayout: IFormLayoutBlocks = [
  {
    layout: [
      {
        path: 'extended_event_analysis.recognize_locals',
        classes: 'label-m n-form-w-6 n-form-pad-10',
        component: NSwitch,
        props: { i18n_label: 'common.recognize_locals' }
      },
      {
        path: 'extended_event_analysis.register_first_appearance',
        classes: 'label-m n-form-w-6 n-form-pad-10',
        component: NSwitch,
        props: { i18n_label: 'common.register_first_appearance' }
      },
      {
        component: ExtendedEventAnalysisExtraSettings,
        classes: 'n-form-w-6 n-form-pad-10 control-m',
        props: (item: CameraGroup) => ({ item })
      }
    ],
    display: (model: any) => {
      return configModule.features.extended_event_analysis_enabled && !!model.extended_event_analysis?.active && model.id !== -1;
    }
  }
];

export function getCameraGroupInformationSchema(): IFormLayoutBlocks {
  return [
    ...cameraGroupInformation,
    ...extendedEventAnalysisActiveLayout,
    ...extendedEventAnalysisFieldsLayout,
    ...bottomLayout
  ];
}
